import { addDisposableListener, Emitter } from '../../../events';
import { CompositeDisposable } from '../../../lifecycle';
import { getPanelData, LocalSelectionTransfer, PanelTransfer, } from '../../../dnd/dataTransfer';
import { toggleClass } from '../../../dom';
import { Droptarget, } from '../../../dnd/droptarget';
import { DragHandler } from '../../../dnd/abstractDragHandler';
import { addGhostImage } from '../../../dnd/ghost';
class TabDragHandler extends DragHandler {
    constructor(element, accessor, group, panel, disabled) {
        super(element, disabled);
        this.accessor = accessor;
        this.group = group;
        this.panel = panel;
        this.panelTransfer = LocalSelectionTransfer.getInstance();
    }
    getData(event) {
        this.panelTransfer.setData([new PanelTransfer(this.accessor.id, this.group.id, this.panel.id)], PanelTransfer.prototype);
        return {
            dispose: () => {
                this.panelTransfer.clearData(PanelTransfer.prototype);
            },
        };
    }
}
export class Tab extends CompositeDisposable {
    get element() {
        return this._element;
    }
    constructor(panel, accessor, group) {
        super();
        this.panel = panel;
        this.accessor = accessor;
        this.group = group;
        this.content = undefined;
        this._direction = 'horizontal';
        this._onPointDown = new Emitter();
        this.onPointerDown = this._onPointDown.event;
        this._onTabClick = new Emitter();
        this.onTabClick = this._onTabClick.event;
        this._onDropped = new Emitter();
        this.onDrop = this._onDropped.event;
        this._onDragStart = new Emitter();
        this.onDragStart = this._onDragStart.event;
        this._onDragEnd = new Emitter();
        this.onDragEnd = this._onDragEnd.event;
        this._element = document.createElement('div');
        this._element.className = 'dv-tab';
        this._element.tabIndex = 0;
        this._element.draggable = !this.accessor.options.disableDnd;
        toggleClass(this.element, 'dv-inactive-tab', true);
        this.dragHandler = new TabDragHandler(this._element, this.accessor, this.group, this.panel, !!this.accessor.options.disableDnd);
        this.dropTarget = new Droptarget(this._element, {
            acceptedTargetZones: ['left', 'right'],
            overlayModel: this._buildOverlayModel(),
            canDisplayOverlay: (event, position) => {
                var _a;
                if (this.group.locked) {
                    return false;
                }
                const data = getPanelData();
                if (data && this.accessor.id === data.viewId) {
                    if (((_a = this.accessor.options.theme) === null || _a === void 0 ? void 0 : _a.tabAnimation) === 'smooth') {
                        // When smooth reorder is enabled, the Tabs
                        // container handles all intra-accessor drops
                        // (both same-group and cross-group) via
                        // animation.  Suppress the per-tab overlay so
                        // the tab is dropped *beside* rather than *on*.
                        return false;
                    }
                    return true;
                }
                return this.group.model.canDisplayOverlay(event, position, 'tab');
            },
            getOverrideTarget: () => { var _a; return (_a = group.model.dropTargetContainer) === null || _a === void 0 ? void 0 : _a.model; },
        });
        this.onWillShowOverlay = this.dropTarget.onWillShowOverlay;
        this.addDisposables(this._onPointDown, this._onTabClick, this._onDropped, this._onDragStart, this._onDragEnd, this.accessor.onDidOptionsChange(() => {
            this.dropTarget.setOverlayModel(this._buildOverlayModel());
        }), this.dragHandler.onDragStart((event) => {
            var _a;
            if (event.dataTransfer) {
                const style = getComputedStyle(this.element);
                const newNode = this.element.cloneNode(true);
                const isVertical = this._direction === 'vertical';
                /**
                 * Properties to skip when copying computed styles for a
                 * vertical tab ghost.  `writing-mode` is excluded so we
                 * can force `horizontal-tb`.  Size and margin logical
                 * properties are excluded because their physical meaning
                 * flips when writing-mode changes, which would produce
                 * incorrect dimensions.
                 */
                const verticalSkip = new Set([
                    'writing-mode',
                    'inline-size',
                    'block-size',
                    'min-inline-size',
                    'min-block-size',
                    'max-inline-size',
                    'max-block-size',
                    'margin-inline',
                    'margin-inline-start',
                    'margin-inline-end',
                    'margin-block',
                    'margin-block-start',
                    'margin-block-end',
                    'padding-inline',
                    'padding-inline-start',
                    'padding-inline-end',
                    'padding-block',
                    'padding-block-start',
                    'padding-block-end',
                ]);
                Array.from(style).forEach((key) => {
                    if (isVertical && verticalSkip.has(key)) {
                        return;
                    }
                    newNode.style.setProperty(key, style.getPropertyValue(key), style.getPropertyPriority(key));
                });
                if (isVertical) {
                    // Force horizontal text flow and swap the physical
                    // dimensions so the ghost appears as a horizontal tab.
                    newNode.style.setProperty('writing-mode', 'horizontal-tb');
                    newNode.style.setProperty('width', style.height);
                    newNode.style.setProperty('height', style.width);
                }
                newNode.style.position = 'absolute';
                newNode.classList.add('dv-tab-ghost-drag');
                addGhostImage(event.dataTransfer, newNode, {
                    y: -10,
                    x: 30,
                });
            }
            this._onDragStart.fire(event);
            if (((_a = this.accessor.options.theme) === null || _a === void 0 ? void 0 : _a.tabAnimation) === 'smooth') {
                // Delay collapse to next frame so the browser
                // captures the full drag image first
                requestAnimationFrame(() => {
                    toggleClass(this.element, 'dv-tab--dragging', true);
                });
            }
        }), addDisposableListener(this._element, 'dragend', (event) => {
            toggleClass(this.element, 'dv-tab--dragging', false);
            this._onDragEnd.fire(event);
        }), this.dragHandler, addDisposableListener(this._element, 'pointerdown', (event) => {
            this._onPointDown.fire(event);
        }), addDisposableListener(this._element, 'click', (event) => {
            this._onTabClick.fire(event);
        }), addDisposableListener(this._element, 'contextmenu', (event) => {
            this.accessor.contextMenuController.show(this.panel, this.group, event);
        }), this.dropTarget.onDrop((event) => {
            this._onDropped.fire(event);
        }), this.dropTarget);
    }
    setActive(isActive) {
        toggleClass(this.element, 'dv-active-tab', isActive);
        toggleClass(this.element, 'dv-inactive-tab', !isActive);
    }
    setContent(part) {
        if (this.content) {
            this._element.removeChild(this.content.element);
        }
        this.content = part;
        this._element.appendChild(this.content.element);
    }
    _buildOverlayModel() {
        var _a;
        // 'line' themes render a 4px insertion strip at the tab edge via the
        // anchor container's small-boundary path.  'fill' themes render a
        // half-width highlighted area, so we disable the small-boundary path
        // entirely (boundary = 0 ⟹ isSmall always false).
        const smallBoundary = ((_a = this.accessor.options.theme) === null || _a === void 0 ? void 0 : _a.dndTabIndicator) === 'line'
            ? Number.POSITIVE_INFINITY
            : 0;
        return {
            activationSize: { value: 50, type: 'percentage' },
            smallWidthBoundary: smallBoundary,
            smallHeightBoundary: smallBoundary,
        };
    }
    setDirection(direction) {
        this._direction = direction;
        this.dropTarget.setTargetZones(direction === 'vertical' ? ['top', 'bottom'] : ['left', 'right']);
    }
    updateDragAndDropState() {
        this._element.draggable = !this.accessor.options.disableDnd;
        this.dragHandler.setDisabled(!!this.accessor.options.disableDnd);
    }
}
